import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import { Skeleton, SkeletonCard, SkeletonTable } from "../ui/skeleton";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
} from "../ui/clean-table";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Building2, ArrowRight, UserCheck, Calendar, Plus } from "lucide-react";
import { useUser } from "../../contexts/UserContext";
import { BusinessUnitService } from "../../services/businessUnitService";
import { getApiUrl } from "../../config/api";
import PageHeader from "../Common/PageHeader";
import CreateBusinessUnit from "./CreateBusinessUnit";

const BusinessUnitSelectionPage = ({
  mode = "user-selection", // "user-selection" or "organization-management"
  organizationId = null,
  pageTitle = null,
  pageDescription = null,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { organizationId: urlOrganizationId } = useParams();
  const {
    isBusinessUnitHead,
    isBusinessUser,
    isOrgAdmin,
    canManageOrganization,
  } = useUser();

  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [organizationData, setOrganizationData] = useState(null);

  // Determine the actual organization ID to use
  const actualOrganizationId = organizationId || urlOrganizationId;

  // Determine if we're in organization management mode
  const isOrganizationMode =
    mode === "organization-management" || actualOrganizationId;
  const canManage = isOrganizationMode
    ? canManageOrganization(actualOrganizationId)
    : false;

  const fetchOrganizationData = async () => {
    if (!isOrganizationMode || !actualOrganizationId) return;

    try {
      const response = await fetch(
        getApiUrl(`organizations/${actualOrganizationId}`),
        {
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOrganizationData(data);
      }
    } catch (err) {
      console.error("Error fetching organization data:", err);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      setLoading(true);
      let data = [];

      if (isOrganizationMode && actualOrganizationId) {
        // Organization management mode - fetch all business units for the organization
        data = await BusinessUnitService.getBusinessUnitsForOrganizationSummary(
          actualOrganizationId,
          getAccessTokenSilently
        );
      } else {
        // User selection mode - fetch user's business units
        data = await BusinessUnitService.getMyBusinessUnits(
          getAccessTokenSilently
        );
      }

      setBusinessUnits(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching business units:", err);
      setError(err.message);
      setBusinessUnits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessUnits();
    if (isOrganizationMode) {
      fetchOrganizationData();
    }
  }, [
    getAccessTokenSilently,
    isBusinessUnitHead,
    isBusinessUser,
    actualOrganizationId,
    isOrganizationMode,
  ]);

  const handleSelectBusinessUnit = (businessUnit) => {
    navigate(`/business-unit/${businessUnit.id}/dashboard`);
  };

  const handleCreateSuccess = () => {
    setIsCreateDialogOpen(false);
    fetchBusinessUnits(); // Refresh the list
  };

  const getUserRoleForBusinessUnit = () => {
    if (isBusinessUnitHead) {
      return "Business Unit Head";
    } else if (isBusinessUser) {
      return "Business User";
    }
    return "Member";
  };

  const getRoleVariant = () => {
    if (isBusinessUnitHead) {
      return "default";
    } else if (isBusinessUser) {
      return "secondary";
    }
    return "outline";
  };

  // Determine page title and description
  const getPageTitle = () => {
    if (pageTitle) return pageTitle;
    return isOrganizationMode ? "Business Units" : "My Business Units";
  };

  const getPageDescription = () => {
    if (pageDescription) return pageDescription;
    return isOrganizationMode
      ? "Manage business units for this organization"
      : "Choose a business unit to work with";
  };

  const getEmptyStateMessage = () => {
    if (isOrganizationMode) {
      return "No business units have been created for this organization yet.";
    }

    if (isBusinessUnitHead) {
      return "You are not currently assigned as a head of any business units. Contact your organization administrator to be assigned as a business unit head.";
    } else if (isBusinessUser) {
      return "You are not currently assigned to any business units. Contact your business unit head or organization administrator to be assigned to a business unit.";
    }

    return "No business units found.";
  };

  if (loading) {
    return (
      <div
        className={
          isOrganizationMode ? "p-10 w-full" : "container mx-auto px-6 py-8"
        }
      >
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          {isOrganizationMode && <Skeleton className="h-9 w-40" />}
        </div>

        {/* Content Skeleton */}
        {isOrganizationMode ? (
          /* Organization Management Mode - Table Skeleton */
          <div className="space-y-4">
            <SkeletonTable rows={5} columns={4} />
          </div>
        ) : (
          /* User Selection Mode - Card Skeleton */
          <div className="grid gap-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <SkeletonCard key={index}>
                <div className="flex items-center justify-between p-6">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-48" />
                      <Skeleton className="h-4 w-32" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="h-5 w-20" />
                      </div>
                    </div>
                  </div>
                  <Skeleton className="h-10 w-32" />
                </div>
              </SkeletonCard>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={
          isOrganizationMode ? "p-10 w-full" : "container mx-auto px-6 py-8"
        }
      >
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className={
        isOrganizationMode
          ? "p-10 w-full"
          : "container mx-auto px-6 py-8 max-w-4xl"
      }
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <PageHeader
          pageName={getPageTitle()}
          entityName={organizationData?.name}
          entityType={isOrganizationMode ? "organization" : undefined}
        />
        {canManage && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Business Unit
          </Button>
        )}
      </div>

      {businessUnits.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              No Business Units Found
            </h3>
            <p className="text-muted-foreground mb-4">
              {getEmptyStateMessage()}
            </p>
            {canManage && (
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                Get Started with Business Units
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Organization Management Mode - Table View */}
          {isOrganizationMode ? (
            <div className="space-y-4">
              <TooltipProvider>
                <CleanTable>
                  <CleanTableHeader>
                    <CleanTableRow>
                      <CleanTableHead>Business Unit</CleanTableHead>
                      <CleanTableHead>Unit Heads</CleanTableHead>
                      <CleanTableHead className="text-center">
                        Total Risks
                      </CleanTableHead>
                      <CleanTableHead className="text-right">
                        Dashboard
                      </CleanTableHead>
                    </CleanTableRow>
                  </CleanTableHeader>
                  <CleanTableBody>
                    {businessUnits.map((unit) => (
                      <CleanTableRow key={unit.id}>
                        <CleanTableCell>
                          <div>
                            <p className="font-medium text-gray-900">
                              {unit.name}
                            </p>
                          </div>
                        </CleanTableCell>
                        <CleanTableCell>
                          <div className="flex items-center">
                            {unit.heads && unit.heads.length > 0 ? (
                              <div className="flex -space-x-2">
                                {unit.heads.map((head, index) => (
                                  <Tooltip key={head.id || index}>
                                    <TooltipTrigger>
                                      <Avatar className="h-8 w-8 border-2 border-white hover:z-10 relative">
                                        <AvatarImage
                                          src={head.profile_pic}
                                          alt={head.name}
                                        />
                                        <AvatarFallback className="text-xs">
                                          {head.name
                                            .split(" ")
                                            .map((n) => n[0])
                                            .join("")
                                            .toUpperCase()}
                                        </AvatarFallback>
                                      </Avatar>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{head.name}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-500">
                                No heads assigned
                              </span>
                            )}
                          </div>
                        </CleanTableCell>
                        <CleanTableCell className="text-center">
                          <span className="text-gray-900 font-medium">
                            {unit.risk_count || 0}
                          </span>
                        </CleanTableCell>
                        <CleanTableCell className="text-right">
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleSelectBusinessUnit(unit)}
                            className="p-0 h-auto"
                          >
                            View Dashboard →
                          </Button>
                        </CleanTableCell>
                      </CleanTableRow>
                    ))}
                  </CleanTableBody>
                </CleanTable>
              </TooltipProvider>
            </div>
          ) : (
            /* User Selection Mode - Card View */
            <div className="grid gap-4">
              {businessUnits.map((businessUnit) => (
                <Card
                  key={businessUnit.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-1">
                          {businessUnit.name}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>
                              Created{" "}
                              {new Date(
                                businessUnit.created_at
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge
                            variant={getRoleVariant()}
                            className="flex items-center gap-1"
                          >
                            <UserCheck className="h-3 w-3" />
                            {getUserRoleForBusinessUnit()}
                          </Badge>
                          {isBusinessUnitHead && (
                            <Badge variant="outline" className="text-xs">
                              Full Access
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleSelectBusinessUnit(businessUnit)}
                      variant="default"
                      size="lg"
                    >
                      {isBusinessUnitHead ? "Manage Unit" : "View Dashboard"}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Summary Stats - Only for user selection mode */}
          {!isOrganizationMode && businessUnits.length > 0 && (
            <div className="mt-8 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {isBusinessUnitHead ? "Managing" : "Member of"}{" "}
                  {businessUnits.length} business unit
                  {businessUnits.length !== 1 ? "s" : ""}
                </span>
                <Badge variant="outline">{getUserRoleForBusinessUnit()}</Badge>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Business Unit Dialog - Only for organization mode */}
      {isOrganizationMode && actualOrganizationId && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <CreateBusinessUnit
              organizationId={actualOrganizationId}
              onSuccess={handleCreateSuccess}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BusinessUnitSelectionPage;
